export type Segment = 'inbound' | 'outbound';

const MAX_UPLOAD_BYTES = 4 * 1024 * 1024;

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

export interface FxFile {
  readonly key: string;
  readonly name: string;
  readonly size: number;
  readonly lastModified: string;
}

export interface FxFileListing {
  readonly files: FxFile[];
  /** The partner's allowlisted folders, including ones with no files yet. */
  readonly folders: string[];
}

export interface FxFilesClientOptions {
  readonly baseUrl: string;
  readonly getAuthToken: () => string | undefined;
  readonly staffPartnerId?: string;
}

export interface FxFilesClient {
  list(segment: Segment): Promise<FxFileListing>;
  presignDownload(segment: Segment, key: string): Promise<string>;
  /** `folder` is a partner folder on the partner's allowlist; omit it to upload at the top level. */
  upload(segment: Segment, file: File, folder?: string): Promise<string>;
}

export function createFxFilesClient(opts: FxFilesClientOptions): FxFilesClient {
  function authHeaders(): Record<string, string> {
    const h: Record<string, string> = { 'content-type': 'application/json' };
    const token = opts.getAuthToken();
    if (token) h['authorization'] = `Bearer ${token}`;
    if (opts.staffPartnerId) h['x-fx-partner'] = opts.staffPartnerId;
    return h;
  }
  async function api<T>(path: string, init?: RequestInit): Promise<T> {
    const res = await fetch(`${opts.baseUrl}${path}`, {
      ...init,
      headers: { ...authHeaders(), ...(init?.headers ?? {}) },
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { message?: string };
      throw new Error(body.message ?? `Request failed: ${res.status}`);
    }
    return res.json() as Promise<T>;
  }
  return {
    async list(segment) {
      const { files, folders } = await api<{ files: FxFile[]; folders?: string[] }>(
        `/files?segment=${segment}`,
      );
      // A backend from before folders were listed sends none.
      return { files, folders: folders ?? [] };
    },
    async presignDownload(segment, key) {
      const { url } = await api<{ url: string }>(`/files/presign-download`, {
        method: 'POST',
        body: JSON.stringify({ segment, key }),
      });
      return url;
    },
    async upload(segment, file, folder) {
      if (file.size > MAX_UPLOAD_BYTES) throw new Error('File exceeds the 4MB limit');
      const dataBase64 = await fileToBase64(file);
      const { key } = await api<{ key: string }>(`/files/upload`, {
        method: 'POST',
        body: JSON.stringify({
          segment,
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          dataBase64,
          ...(folder ? { folder } : {}),
        }),
      });
      return key;
    },
  };
}
